import clsx from "clsx";
import { FC, useEffect } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { PrivacyPDF, RASTER_LANDINGS, TermsPDF } from "@ryde/assets-landings";
import {
  CheckboxControlledMui,
  DropdownTemplate,
  MessagesDisplayContainer,
  PhoneTemplate,
  TextboxTemplate,
} from "@ryde/controls";
import {
  apiDataFetchingLandingAtom,
  landingDropdownStationListAtom,
  landingMessagesAtom,
  TnufaLoginStepFormFieldsEnum,
} from "@ryde/utils-landings";

import { ContinueButton } from "../../widgets";
import { useStepLoginTnufa } from "./use-step-login.tnufa";

export const StepLoginTnufa: FC = () => {
  const { methods, unitsDropdownList, handleLogin, isContinueCanBeApply } =
    useStepLoginTnufa();
  const [searchParams, setSearchParams] = useSearchParams();
  const landingMessages = useRecoilValue(landingMessagesAtom);
  const isApiLoading = useRecoilValue(apiDataFetchingLandingAtom);
  const { t } = useTranslation();
  const stationsDropdownList = useRecoilValue(landingDropdownStationListAtom);

  const { watch, setValue } = methods;

  useEffect(() => {
    if (searchParams) {
      setSearchParams("");
    }
  }, [searchParams, setSearchParams]);

  const acceptTermsWatchField = watch(TnufaLoginStepFormFieldsEnum.AcceptTerms);
  const acceptPrivacyPolicyWatchField = watch(
    TnufaLoginStepFormFieldsEnum.AcceptPrivacyPolicy,
  );

  return (
    <FormProvider {...methods}>
      <form noValidate onSubmit={e => e.preventDefault()}>
        <h1 className="landing-title">
          {t("landing.login.title")}
          <br />
          {t("landing.login.subTitle")}
        </h1>

        <div className="landing-form-field">
          <label htmlFor={TnufaLoginStepFormFieldsEnum.FirstName}>
            {t("landing.formLabel.firstName")}*
          </label>
          <TextboxTemplate
            uniqueAttrId={TnufaLoginStepFormFieldsEnum.FirstName}
            name={TnufaLoginStepFormFieldsEnum.FirstName}
          />
        </div>

        <div className="landing-form-field">
          <label htmlFor={TnufaLoginStepFormFieldsEnum.lastName}>
            {t("landing.formLabel.lastName")}*
          </label>
          <TextboxTemplate
            uniqueAttrId={TnufaLoginStepFormFieldsEnum.lastName}
            name={TnufaLoginStepFormFieldsEnum.lastName}
          />
        </div>

        <div className="landing-form-field">
          <label htmlFor={TnufaLoginStepFormFieldsEnum.Unit}>
            {t("landing.formLabel.residentialSettlement")}*
          </label>
          <DropdownTemplate
            uniqueAttrId={TnufaLoginStepFormFieldsEnum.Unit}
            name={TnufaLoginStepFormFieldsEnum.Unit}
            size="medium"
            formAttributes={{
              selectItems: unitsDropdownList,
              skipDropdownItemsTranslation: true,
            }}
          />
        </div>

        <div className="landing-form-field">
          <label htmlFor={TnufaLoginStepFormFieldsEnum.Phone}>
            {t("landing.formLabel.phone")}*
          </label>
          <PhoneTemplate
            uniqueAttrId={TnufaLoginStepFormFieldsEnum.Phone}
            name={TnufaLoginStepFormFieldsEnum.Phone}
            sx={{ size: "small" }}
            formAttributes={{ disablePhoneCallable: true }}
          />
        </div>

        <div
          className={clsx("landing-form-field", {
            hidden: !stationsDropdownList?.length,
          })}
        >
          <label htmlFor={TnufaLoginStepFormFieldsEnum.SourceStation2Id}>
            {t("landing.formLabel.sourceStation2IdTnufa")}*
          </label>
          <DropdownTemplate
            uniqueAttrId={TnufaLoginStepFormFieldsEnum.SourceStation2Id}
            name={TnufaLoginStepFormFieldsEnum.SourceStation2Id}
            size="medium"
            formAttributes={{
              selectItems: stationsDropdownList,
              skipDropdownItemsTranslation: true,
            }}
          />
        </div>

        <div className="landing-form-field">
          <div className="landing-accept-terms">
            <CheckboxControlledMui
              uniqueAttrId={TnufaLoginStepFormFieldsEnum.AcceptTerms}
              width={16}
              height={16}
              checked={acceptTermsWatchField ?? false}
              setCheckedToggle={() =>
                setValue(
                  TnufaLoginStepFormFieldsEnum.AcceptTerms,
                  !acceptTermsWatchField,
                )
              }
            />
            <span className="custom-label">
              {t("landing.formLabel.termsAndConditions")}{" "}
              <a
                href={TermsPDF}
                target="_blank"
                rel="noreferrer"
                aria-label={t("landing.formLabel.termsAndConditions")}
              >
                כאן
              </a>
            </span>
          </div>
        </div>

        <div className="landing-form-field">
          <div className="landing-accept-terms">
            <CheckboxControlledMui
              uniqueAttrId={TnufaLoginStepFormFieldsEnum.AcceptPrivacyPolicy}
              width={16}
              height={16}
              checked={acceptPrivacyPolicyWatchField ?? false}
              setCheckedToggle={() =>
                setValue(
                  TnufaLoginStepFormFieldsEnum.AcceptPrivacyPolicy,
                  !acceptPrivacyPolicyWatchField,
                )
              }
            />
            <span className="custom-label">
              {t("landing.formLabel.acceptPrivacyPolicy")}{" "}
              <a
                href={PrivacyPDF}
                target="_blank"
                rel="noreferrer"
                aria-label={t("landing.formLabel.acceptPrivacyPolicy")}
              >
                כאן
              </a>
            </span>
          </div>
        </div>

        <MessagesDisplayContainer
          serverMessages={landingMessages?.messages}
          type={landingMessages?.type}
        />

        <ContinueButton
          loading={isApiLoading}
          disabled={!isContinueCanBeApply}
          onClick={handleLogin}
        />

        <div className="landing-container-footer">
          <div className="landing-container-footer-logo">
            <img
              className="landing-logo__img"
              src={RASTER_LANDINGS.RideLogoPNG}
              alt="Ryde logo"
              width="120"
              height="51"
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
